/**
 * VoiceNavButton.jsx — floating mic that listens for voice commands and
 * navigates the SPA. Uses the browser-native Web Speech API (free, no
 * SDK), works offline, no key required.
 *
 * Commands (user-spec Feb 26 2026): "go to <X>", "open <X>", "search
 * <query>" / "find <query>". The latter deep-links the library search.
 *
 * Tap once → mic opens, listens for ~5s, executes. Tap again to cancel.
 * Gracefully no-ops on browsers without webkitSpeechRecognition.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ROUTES = {
  // Trigger words → route. First match wins.
  library: '/library',
  books: '/library',
  audiobook: '/library?tab=audiobooks',
  audiobooks: '/library?tab=audiobooks',
  audio: '/library?tab=audiobooks',
  cinema: '/library?tab=cinematics',
  cinematic: '/library?tab=cinematics',
  cinematics: '/library?tab=cinematics',
  discover: '/discover',
  home: '/discover',
  profile: '/profile',
  account: '/profile/account',
  notifications: '/profile/notifications',
  security: '/profile/security',
  stats: '/profile/stats',
  earnings: '/profile/earnings',
  achievements: '/profile/achievements',
  inbox: '/inbox',
  messages: '/inbox',
  dashboard: '/dashboard',
  progress: '/dashboard',
  vault: '/vault',
  premium: '/premium',
  upgrade: '/premium',
  upload: '/upload',
  bookclub: '/bookclub',
  club: '/bookclub',
  admin: '/admin',
};

const useSpeechRecognition = () => {
  const recRef = useRef(null);
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.continuous = false;
    recRef.current = rec;
  }, []);
  return recRef;
};

const VoiceNavButton = () => {
  const navigate = useNavigate();
  const recRef = useSpeechRecognition();
  const [listening, setListening] = useState(false);
  const supported = typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const start = () => {
    if (!supported || !recRef.current) {
      toast.error('Voice not supported on this browser. Try Chrome or Safari.');
      return;
    }
    if (listening) {
      recRef.current.stop();
      setListening(false);
      return;
    }
    setListening(true);
    const rec = recRef.current;
    rec.onresult = (ev) => {
      const transcript = (ev.results?.[0]?.[0]?.transcript || '').toLowerCase().trim();
      handleCommand(transcript);
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error && e.error !== 'aborted' && e.error !== 'no-speech') {
        toast.error(`Voice: ${e.error}`);
      }
    };
    rec.onend = () => setListening(false);
    try {
      rec.start();
    } catch {
      setListening(false);
    }
  };

  const handleCommand = (text) => {
    if (!text) return;
    toast.success(`Heard: "${text}"`);

    // Search intent — "search/find <query>"
    const searchMatch = text.match(/^(?:search|find|look for)\s+(.+)$/);
    if (searchMatch) {
      const q = searchMatch[1].trim();
      navigate(`/library?q=${encodeURIComponent(q)}`);
      return;
    }
    // "Go to / open <X>"
    const navMatch = text.match(/^(?:go to|open|show me?|navigate to|take me to)\s+(.+)$/);
    const candidate = navMatch ? navMatch[1] : text;
    // Try first whole phrase, then each word, against ROUTES.
    const words = candidate.split(/\s+/);
    for (const phrase of [candidate, ...words]) {
      const clean = phrase.replace(/[^a-z]/g, '');
      if (ROUTES[clean]) {
        navigate(ROUTES[clean]);
        return;
      }
    }
    toast.error(`I didn't catch a command. Try "go to library" or "search romance".`);
  };

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={start}
      className={`fixed bottom-24 right-4 z-40 p-3 rounded-full shadow-lg transition-all ${
        listening
          ? 'bg-red-500 text-white animate-pulse'
          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
      }`}
      aria-label={listening ? 'Stop listening' : 'Voice navigation'}
      data-testid="voice-nav-btn"
      title={listening ? 'Listening…' : 'Voice navigation'}
    >
      {listening ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
    </button>
  );
};

export default VoiceNavButton;
